// Sign text render (MB.1). Draws a sign's four lines as a camera-facing board using
// the pure glyph rasterizer (sim/mesh/sign_glyph) uploaded as a DataTexture — the
// "canvas-texture onto the board quad" finish. Sprites are pooled by cell key and
// re-rasterized only when the text changes; blank signs show nothing. Client-only.
import * as THREE from 'three';
import { rasterizeSign, SIGN_COLS, SIGN_ROWS } from '../../sim/mesh/sign_glyph';

interface Entry { sprite: THREE.Sprite; tex: THREE.DataTexture; hash: string; }

export class SignTextRenderer {
  private readonly group = new THREE.Group();
  private readonly sprites = new Map<string, Entry>();

  constructor(scene: THREE.Scene) { scene.add(this.group); }

  /** Sync the rendered boards to the current sign-text map (cellKey -> lines).
   *  `glowing` names the cells that have had glow ink applied (M22): their glyphs
   *  are rasterized bright with a halo and rendered unlit, so they read at night. */
  update(signText: ReadonlyMap<string, readonly string[]>, glowing?: ReadonlySet<string>): void {
    const seen = new Set<string>();
    for (const [key, lines] of signText) {
      const glow = glowing?.has(key) ?? false;
      const hash = `${glow ? 'glow:' : ''}${lines.join('\n')}`;
      if (lines.join('\n').trim() === '') continue; // blank -> render nothing
      seen.add(key);
      const existing = this.sprites.get(key);
      if (existing && existing.hash === hash) continue;
      const { width, height, data } = rasterizeSign(lines, undefined, glow);
      // rasterizeSign allocates a fresh ArrayBuffer-backed array (TS 5.7 pins the
      // DataTexture buffer type to ArrayBuffer).
      const tex = new THREE.DataTexture(data as Uint8Array<ArrayBuffer>, width, height, THREE.RGBAFormat);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.flipY = true; // signPixels' y grows down; textures sample bottom-up
      tex.needsUpdate = true;
      if (existing) {
        existing.tex.dispose();
        const mat = existing.sprite.material as THREE.SpriteMaterial;
        mat.map = tex; mat.toneMapped = !glow; mat.needsUpdate = true;
        existing.tex = tex; existing.hash = hash;
      } else {
        const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true, toneMapped: !glow });
        const sprite = new THREE.Sprite(mat);
        sprite.scale.set(0.85, (0.85 * SIGN_ROWS) / SIGN_COLS, 1); // keep the 60x24 aspect
        const [x, y, z] = key.split(',').map(Number) as [number, number, number];
        sprite.position.set(x + 0.5, y + 0.5, z + 0.5);
        this.group.add(sprite);
        this.sprites.set(key, { sprite, tex, hash });
      }
    }
    for (const [key, e] of this.sprites) {
      if (seen.has(key)) continue;
      this.group.remove(e.sprite);
      (e.sprite.material as THREE.SpriteMaterial).dispose();
      e.tex.dispose();
      this.sprites.delete(key);
    }
  }

  /** Drop all pooled boards (dimension switch, DIM-1). */
  clear(): void {
    for (const e of this.sprites.values()) {
      this.group.remove(e.sprite);
      (e.sprite.material as THREE.SpriteMaterial).dispose();
      e.tex.dispose();
    }
    this.sprites.clear();
  }
}
